import React, { useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform, useMotionTemplate, useReducedMotion } from 'framer-motion';
import { FiArrowUpRight, FiBookOpen, FiGithub } from 'react-icons/fi';
import SectionHead from '../ui/SectionHead';
import Reveal from '../ui/Reveal';
import Magnetic from '../ui/Magnetic';
import { book, writing } from '../../data/profile';
import './Writing.css';

const ease = [0.16, 1, 0.3, 1];

function BookCover() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const px = useMotionValue(0.5);
  const py = useMotionValue(0.5);
  const sx = useSpring(px, { stiffness: 120, damping: 18, mass: 0.4 });
  const sy = useSpring(py, { stiffness: 120, damping: 18, mass: 0.4 });
  const rotateY = useTransform(sx, [0, 1], [-14, 14]);
  const rotateX = useTransform(sy, [0, 1], [10, -10]);
  const glareX = useTransform(sx, [0, 1], [0, 100]);
  const glareY = useTransform(sy, [0, 1], [0, 100]);
  const glare = useMotionTemplate`radial-gradient(circle at ${glareX}% ${glareY}%, rgba(255,255,255,0.22), rgba(255,255,255,0) 55%)`;

  const onMove = (e) => {
    if (reduce) return;
    const r = ref.current.getBoundingClientRect();
    px.set((e.clientX - r.left) / r.width);
    py.set((e.clientY - r.top) / r.height);
  };

  const onLeave = () => {
    px.set(0.5);
    py.set(0.5);
  };

  return (
    <div className="book__stage" ref={ref} onMouseMove={onMove} onMouseLeave={onLeave}>
      <div className="book__glow" aria-hidden />
      <motion.div
        className="book__float"
        animate={reduce ? {} : { y: [0, -10, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      >
        <motion.div
          className="book__cover"
          style={{ rotateX: reduce ? 0 : rotateX, rotateY: reduce ? 0 : rotateY }}
          initial={reduce ? false : { opacity: 0, y: 40, rotateY: -30 }}
          whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.2, ease }}
        >
          <picture>
            <source srcSet={book.cover.webp} type="image/webp" />
            <img src={book.cover.jpg} alt={book.cover.alt} loading="lazy" />
          </picture>
          <motion.div className="book__glare" style={{ background: glare }} aria-hidden />
          <div className="book__spine" aria-hidden />
        </motion.div>
      </motion.div>
      <Reveal className="book__badge" delay={0.5} y={12}>
        <span className="mono">{book.year}</span>
        <span className="book__badge-sep" />
        <span className="mono">{book.formats.join(' & ')}</span>
      </Reveal>
    </div>
  );
}

const chapterList = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
};

const chapterItem = {
  hidden: { opacity: 0, x: -14 },
  show: { opacity: 1, x: 0, transition: { duration: 0.6, ease } },
};

export default function Writing() {
  return (
    <section className="section writing" id="writing">
      <div className="container">
        <SectionHead
          index="06"
          label="Writing"
          title={[{ text: 'I also' }, { text: 'write', em: true }, { text: 'things down.' }]}
          aside={<p className="small">A book for engineers shipping with AI agents, essays on dev.to, and a peer-reviewed paper.</p>}
        />

        <div className="book">
          <BookCover />

          <div className="book__info">
            <Reveal as="p" className="eyebrow" y={12}>
              The book <span className="book__sep">/</span> {book.tagline}
            </Reveal>

            <Reveal delay={0.05}>
              <h3 className="book__title">{book.title}</h3>
              <p className="book__subtitle">{book.subtitle}</p>
            </Reveal>

            <Reveal as="p" className="book__blurb" delay={0.1}>
              {book.blurb}
            </Reveal>

            <Reveal as="p" className="body book__desc" delay={0.15}>
              {book.description}
            </Reveal>

            <Reveal className="book__themes" delay={0.2}>
              {book.themes.map((t) => (
                <span key={t} className="pill pill--tan">{t}</span>
              ))}
            </Reveal>

            <div className="book__inside">
              <Reveal className="book__inside-head" y={10}>
                <span className="mono">Inside</span>
                <span className="mono book__count">{book.chapters.length} chapters and counting</span>
              </Reveal>
              <motion.ol
                className="book__chapters"
                variants={chapterList}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.3 }}
              >
                {book.chapters.map((c, i) => (
                  <motion.li key={c} variants={chapterItem}>
                    <span className="mono accent">{String(i + 1).padStart(2, '0')}</span>
                    <span className="book__chapter">{c}</span>
                  </motion.li>
                ))}
              </motion.ol>
            </div>

            <Reveal as="p" className="book__living" delay={0.1}>
              <span className="dot-live" /> {book.living}
            </Reveal>

            <Reveal className="book__actions" delay={0.15}>
              <Magnetic>
                <a href={book.links.kindle} target="_blank" rel="noreferrer" className="btn btn--primary">
                  Get it on Amazon <span className="btn__icon"><FiArrowUpRight /></span>
                </a>
              </Magnetic>
              <Magnetic>
                <a href={book.links.paperback} target="_blank" rel="noreferrer" className="btn btn--ghost">
                  Paperback <span className="btn__icon"><FiArrowUpRight /></span>
                </a>
              </Magnetic>
              <div className="book__links">
                <a href={book.links.sample} target="_blank" rel="noreferrer" className="link link--quiet">
                  <FiBookOpen /> Read chapter 2 free
                </a>
                <a href={book.links.toolkit} target="_blank" rel="noreferrer" className="link link--quiet">
                  <FiGithub /> Rules &amp; loop templates, MIT
                </a>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="essays">
          <Reveal className="essays__head">
            <span className="mono">Essays &amp; research</span>
            <span className="rule" />
          </Reveal>
          <ul className="essays__list">
            {writing.map((w, i) => (
              <Reveal as="li" key={w.title} className="essays__row" delay={i * 0.06} amount={0.3}>
                <a href={w.href} target="_blank" rel="noreferrer" className="essays__link">
                  <span className={`pill ${w.type === 'Paper' ? 'pill--tan' : 'pill--accent'} essays__type`}>{w.type}</span>
                  <div className="essays__main">
                    <h4 className="essays__title">{w.title}</h4>
                    <p className="small essays__desc">{w.description}</p>
                  </div>
                  <div className="essays__meta">
                    <span className="mono">{w.venue}</span>
                    <span className="mono">{w.date}</span>
                  </div>
                  <span className="essays__arrow"><FiArrowUpRight /></span>
                </a>
              </Reveal>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
